import { useEffect, useRef, useState, type RefObject } from "react";
import { useLocation } from "react-router-dom";

/**
 * The navigation drawer: the sidebar below the `md` breakpoint, behind a menu
 * button in the header.
 *
 * Held by the layout because two siblings share it: the header has the button
 * and `NavDrawer` has the panel, and each needs the other's half (the button
 * names the panel in `aria-controls`; closing the panel hands focus back to the
 * button).
 */
export interface NavDrawerState {
  /** The panel's element id, which the button names in `aria-controls`. */
  id: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  /** The menu button, which takes focus back when the panel closes. */
  buttonRef: RefObject<HTMLButtonElement>;
}

/** The panel's id. One drawer per page, so one fixed id is enough. */
const NAV_DRAWER_ID = "app-navigation-drawer";

/** Tailwind's `md`: from here up the sidebar is the fixed rail, not a drawer. */
const RAIL_MEDIA_QUERY = "(min-width: 768px)";

export function useNavDrawer(): NavDrawerState {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  // Any change of page closes it, whether a row in the drawer caused it or Back
  // did: left open, it covers the page it just led to.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Widening past `md` (a tablet turned on its side) brings the fixed rail
  // back. The drawer is modal, so left open it would keep the page inert
  // under a panel the rail has replaced.
  useEffect(() => {
    const query = window.matchMedia(RAIL_MEDIA_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) setOpen(false);
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return { id: NAV_DRAWER_ID, open, setOpen, buttonRef };
}
